"use client";

import React from "react";
import { ChartNoAxesColumnIncreasing, Heart, ListOrdered, Loader2, Lock, Star } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { UserAuth } from "@/components/UserAuth";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import type { LabelKey } from "@/lib/data/labels";

/**
 * The features that requirement 3 restricts to signed-in users.
 */
export type GatedFeature = "recommended" | "favourites" | "priorities" | "quartiles";

export const GATED_FEATURES: Record<
  GatedFeature,
  { icon: React.ComponentType<{ className?: string }>; title: LabelKey; description: LabelKey }
> = {
  recommended: {
    icon: Star,
    title: "recommended",
    description: "signInRequiredRecommended",
  },
  favourites: {
    icon: Heart,
    title: "favourites",
    description: "signInRequiredFavourites",
  },
  priorities: {
    icon: ListOrdered,
    title: "priorities",
    description: "signInRequiredPriorities",
  },
  quartiles: {
    icon: ChartNoAxesColumnIncreasing,
    title: "scoreQuartileFilters",
    description: "signInRequiredScoreQuartileFilters",
  },
};

/**
 * Full-page notice shown in place of a gated feature. The sign-in controls are
 * the same `UserAuth` component the sidebar uses, so Google and email/OTP
 * sign-in stay in one place.
 */
export function SignInRequired({ feature }: { feature: GatedFeature }) {
  const { translate } = useLanguage();
  const { icon: Icon, title, description } = GATED_FEATURES[feature];

  return (
    <div className="narrow-mt px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto">
      <div className="py-10">
        <Card>
          <CardContent className="flex flex-col items-center gap-5 py-10 text-center">
            <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Icon className="h-8 w-8 text-primary" />
              <span className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-background shadow ring-1 ring-border">
                <Lock className="h-4 w-4 text-muted-foreground" />
              </span>
            </div>

            <div className="space-y-3">
              <h1 className="text-2xl font-bold">{translate(title)}</h1>
              {/* Say plainly, before any sign-in attempt, that the feature is
                  for logged-in users. */}
              <p className="max-w-md text-base font-semibold text-foreground">
                {translate("featureLoggedInOnly")}
              </p>
              <p className="max-w-md text-sm text-muted-foreground">
                {translate(description)}
              </p>
            </div>

            <div className="w-full max-w-xs pt-1">
              <UserAuth />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * Renders `children` only for signed-in users; everyone else gets the sign-in
 * notice. The gated page is not mounted at all while signed out, so none of its
 * data loading runs for an anonymous visitor.
 */
export function RequireAuth({
  feature,
  children,
}: {
  feature: GatedFeature;
  children: React.ReactNode;
}) {
  const { status } = useAuth();

  if (status === "loading") {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (status !== "authenticated") {
    return <SignInRequired feature={feature} />;
  }

  return <>{children}</>;
}
